import { useEffect, useMemo, useRef, useState } from "react";
import {
  MessageCircle,
  Shield,
  Truck,
  Star,
  ChevronDown,
  ChevronUp,
  CheckCircle,
} from "lucide-react";
import LocalStorageUtil from "../lib/LocalStorageUtil";
import * as FacebookAPI from "../api/FacebookApi";
import {
  getConfig,
  loadFbPixel,
  createCouponCodeFormFbData,
} from "../api/FacebookApi";
import { sendNotification } from "../api/ConfigAPI";
import "../css/Home.scss";

const WHATSAPP_NUMBER = "8618479738188";
const WA_BASE_MESSAGE =
  "Hello! I'm interested in your luxury and sneaker collection. Can you share available options";

/**
 * 构建带优惠券码的 WA 消息
 * 格式: AdvertisingID--"xxx"\nHello! I'm interested...
 */
const buildWaMessage = (couponCode) => {
  const parts = [];
  if (couponCode) {
    parts.push(`AdvertisingID--"${couponCode}"`);
    parts.push("");
    parts.push("");
  }
  parts.push(WA_BASE_MESSAGE);
  return encodeURIComponent(parts.join("\n"));
};

const getCookieValue = (name) => {
  if (typeof document === "undefined") {
    return "";
  }

  const cookie = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`));

  return cookie ? decodeURIComponent(cookie.split("=")[1]) : "";
};

const setCookieValue = (name, value, days = 1) => {
  if (typeof document === "undefined") {
    return;
  }

  const expires = new Date(
    Date.now() + days * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};

const getEventId = (eventType) => {
  const cookieName = `fb_event_id_${eventType}`;
  const stored = getCookieValue(cookieName);

  if (stored) {
    return stored;
  }

  const id = Math.round(Math.random() * 1e10).toString();
  setCookieValue(cookieName, id, 1);
  return id;
};

// 等待 Facebook cookies 写入完成
const waitForFbCookies = (
  cookieNames = ["_fbp"],
  maxRetries = 3,
  retryInterval = 500,
) => {
  return new Promise((resolve) => {
    const checkCookies = (retryCount = 0) => {
      const cookieValues = {};
      let allReady = true;

      cookieNames.forEach((name) => {
        const value = getCookieValue(name);
        cookieValues[name] = value;
        if (!value || value.trim() === "") {
          allReady = false;
        }
      });

      if (allReady || retryCount >= maxRetries) {
        resolve(cookieValues);
      } else {
        setTimeout(() => checkCookies(retryCount + 1), retryInterval);
      }
    };

    checkCookies();
  });
};

const FB_TRANSFORM_EVENT_NAME = "Contact";
const COUNTRY_CODE = "USA";

const createEmptyUserData = () => ({
  em: "",
  ph: "",
  fn: "",
  ln: "",
  ct: "",
  st: "",
  zp: "",
  country: "",
  external_id: "",
  client_ip_address: "",
  client_user_agent: "",
  fbc: "",
  fbp: "",
  lead_id: "",
  anon_id: "",
  madid: "",
  page_id: "",
  page_scoped_user_id: "",
  ctwa_clid: "",
  ig_account_id: "",
  ig_sid: "",
});

const ASSETS = {
  logo: "/favicon.svg",
  heroBg: "/images/product-1-showroom.jpg",
  showroom: "/images/2.mp4",
  watches: "/images/watches.jpg",
  img7: "/images/831d513b58a72fb6784f5a0a08a0d87e.jpg",
  newImg1: "/images/mens-clothing.jpg",
  newImg2: "/images/womens-clothing.jpg",
  reviewImg1: "/images/4cdc5d333c4f55b269e36bb67d808b64.jpg",
  reviewImg2: "/images/adb227181ed62022811aded875ad0136.jpg",
  reviewImg3: "/images/af1ff2b26ce0175fb4265128390f6d52.jpg",
  reviewImg4: "/images/ba256e629a048b8fdf32fe0d73da37b7.jpg",
  mensShoes: "/images/mens-shoes.jpg",
  womensShoes: "/images/womens-shoes.jpg",
  mensBags: "/images/mens-bags.jpg",
  womensBags: "/images/womens-bags.jpg",
};

function useScrollReveal(isReady) {
  useEffect(() => {
    // 页面内容未就绪时不观察
    if (!isReady) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.12 },
    );

    document.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [isReady]);
}

const products = [
  {
    id: 1,
    title: "Men's Shoes Collection",
    subtitle:
      "Explore our extensive collection of men's footwear, including all Air Jordan series, Louis Vuitton, and more premium brands. Contact us to get the full catalog.",
    media: ASSETS.mensShoes,
    tag: "Men's Shoes",
  },
  {
    id: 2,
    title: "Women's Shoes Collection",
    subtitle:
      "Discover elegant and stylish women's shoes, featuring exclusive releases from top luxury designers like LV and Chanel. Contact us to get the full catalog.",
    media: ASSETS.womensShoes,
    tag: "Women's Shoes",
  },
  {
    id: 3,
    title: "Men's Bags Collection",
    subtitle:
      "Premium men's bags for every occasion. From classic Louis Vuitton briefcases to modern crossbody styles. Contact us to get the full catalog.",
    media: ASSETS.mensBags,
    tag: "Men's Bags",
  },
  {
    id: 4,
    title: "Women's Bags Collection",
    subtitle:
      "Iconic women's handbags featuring timeless pieces from Chanel, Louis Vuitton, Dior, and Hermes. Contact us to get the full catalog.",
    media: ASSETS.womensBags,
    tag: "Women's Bags",
  },
  {
    id: 6,
    title: "Men's Clothing",
    subtitle:
      "Elevate your wardrobe with our curated selection of men's luxury apparel and streetwear. Contact us to get the full catalog.",
    media: ASSETS.newImg1,
    tag: "Men's Clothing",
  },
  {
    id: 7,
    title: "Women's Clothing",
    subtitle:
      "Sophisticated women's fashion pieces, blending contemporary trends with classic elegance. Contact us to get the full catalog.",
    media: ASSETS.newImg2,
    tag: "Women's Clothing",
  },
  {
    id: 8,
    title: "Luxury Watches",
    subtitle:
      "Exquisite timepieces from world-renowned watchmakers, including Rolex, Patek Philippe, and AP. Contact us to get the full catalog.",
    media: ASSETS.watches,
    tag: "Watches",
  },
  {
    id: 10,
    title: "Premium Accessories",
    subtitle:
      "Complete your look with our luxury accessories, from statement belts to fine jewelry. Contact us to get the full catalog.",
    media: ASSETS.img7,
    tag: "Accessories",
  },
];

const trustFeatures = [
  {
    icon: Shield,
    title: "Secure PayPal Payments",
    desc: "We use PayPal throughout the process to ensure the safety of your funds. Your privacy and security are our top priority.",
  },
  {
    icon: Truck,
    title: "FedEx Shipping & Customs Guarantee",
    desc: "Enjoy fast and reliable shipping via FedEx. Customs seizure guarantee: If your package is detained by customs, we guarantee a full refund or free reshipment.",
  },
  {
    icon: Star,
    title: "1:1 Replica Quality & After-Sales",
    desc: "We guarantee top-tier 1:1 replica quality for every piece. Plus, enjoy peace of mind with our 1-month worry-free after-sales policy.",
  },
];

const reviews = [
  {
    id: 1,
    name: "James Whitmore",
    location: "New York, NY",
    date: "1 weeks ago",
    title: "Really impressed",
    rating: 5,
    text: "Really impressed with these. I've bought replicas before and these are easily the best I've seen. Leather feels quality and they're comfortable too. Took about 10 days to New York.",
    avatar: "JW",
    image: "/images/af1ff2b26ce0175fb4265128390f6d52.jpg",
    likes: 34,
  },
  {
    id: 2,
    name: "Charlotte Reynolds",
    location: "Los Angeles, CA",
    date: "2 weeks ago",
    title: "Quality hardware",
    rating: 5,
    text: "Ordered this for a night out and got loads of compliments. The hardware has the right weight, doesn't feel cheap at all. Packaging was discreet which I appreciated. Will definitely be back.",
    avatar: "CR",
    image: "/images/6f546ee246c9f50eba6a2bcb6ccd755c.jpg",
    likes: 21,
  },
  {
    id: 3,
    name: "Michael T.",
    location: "Miami, FL",
    date: "3 weeks ago",
    title: "Perfect fit and quality",
    rating: 5,
    text: "The fabric of this shirt is incredible, exactly like the original. Stitching is flawless and the fit is spot on. WhatsApp communication was professional and shipping via FedEx took about 8 days.",
    avatar: "MT",
    image: "/images/6f59678f14ba7be2c2faf4b069c46b77.jpg",
    likes: 45,
  },
  {
    id: 4,
    name: "Sarah Jenkins",
    location: "Chicago, IL",
    date: "1 month ago",
    title: "Exceeded expectations",
    rating: 5,
    text: "Needed some solid sneakers and this exceeded my expectations. The quality is insane. Arrived in Chicago in 9 days without any customs issues. Highly recommend their service.",
    avatar: "SJ",
    image: "/images/928084dffede6bcff137aecde108e952.jpg",
    likes: 56,
  },
  {
    id: 5,
    name: "David R.",
    location: "Houston, TX",
    date: "1 month ago",
    title: "Perfect full outfit setup",
    rating: 5,
    text: "Got the full Prada set - nylon jacket, pants, and the America's Cup sneakers. The material feel is identical to retail, down to the metal triangle logos. Everything fits true to size. Very satisfied with the FedEx shipping speed.",
    avatar: "DR",
    image: "/images/c4e380ce852af2ef858e0f452316cb54.jpg",
    likes: 89,
  },
  {
    id: 6,
    name: "Emma W.",
    location: "Seattle, WA",
    date: "2 months ago",
    title: "Perfect customer service",
    rating: 5,
    text: "Absolutely in love with my new purchase! The customer service team was so helpful in finding the exact size I needed. Will be ordering more soon.",
    avatar: "EW",
    image: "/images/df85546c5a4abca51811176f9c985efe.jpg",
    likes: 112,
  },
];

const faqs = [
  {
    q: "How do I place an order?",
    a: "Simply click the WhatsApp button and our specialist will guide you through the entire process. We'll help you select the perfect piece and handle everything from there.",
  },
  {
    q: "How long does shipping take?",
    a: "We ship all orders via FedEx. For US customers, typical delivery is 7-10 business days. We provide real-time tracking updates via WhatsApp.",
  },
  {
    q: "What payment methods do you accept?",
    a: "We accept PayPal as our primary payment method for secure and convenient transactions. Our team will guide you through the payment process privately via WhatsApp.",
  },
  {
    q: "What is your return and exchange policy?",
    a: "We offer a 1-month worry-free after-sales policy. If you are not satisfied with your purchase or need a different size, please contact us via WhatsApp within 30 days of receiving your item to initiate a return or exchange.",
  },
];

const processSteps = [
  {
    step: "01",
    title: "Contact Us",
    desc: "Click the WhatsApp button and send us a message. Our team responds within minutes.",
  },
  {
    step: "02",
    title: "Browse & Select",
    desc: "Our specialist shares available collections and helps you find the perfect piece.",
  },
  {
    step: "03",
    title: "Confirm & Pay",
    desc: "We guide you through a simple, secure ordering process via PayPal at your convenience.",
  },
  {
    step: "04",
    title: "Receive & Enjoy",
    desc: "Your order is shipped with tracking updates sent directly to your WhatsApp.",
  },
];

const benefits = [
  "The Art Of 1:1 Replication",
  "Same Materials",
  "Same Factories",
  "Indistinguishable Quality",
];

const heroBadges = ["Fast Response", "Private & Secure", "US Support"];

const delay80Classes = [
  "home-delay-80-0",
  "home-delay-80-1",
  "home-delay-80-2",
  "home-delay-80-3",
  "home-delay-80-4",
  "home-delay-80-5",
  "home-delay-80-6",
  "home-delay-80-7",
  "home-delay-80-8",
  "home-delay-80-9",
  "home-delay-80-10",
];

const delay100Classes = [
  "home-delay-100-0",
  "home-delay-100-1",
  "home-delay-100-2",
  "home-delay-100-3",
];

const getDelay80Class = (index) =>
  delay80Classes[Math.min(index, delay80Classes.length - 1)];

const getDelay100Class = (index) =>
  delay100Classes[Math.min(index, delay100Classes.length - 1)];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="home-faq-item fade-up">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="home-faq-item__button"
        aria-expanded={open}
      >
        <span className="home-faq-item__question">{q}</span>
        {open ? (
          <ChevronUp size={18} className="home-faq-item__icon" />
        ) : (
          <ChevronDown size={18} className="home-faq-item__icon" />
        )}
      </button>
      {open && <p className="home-faq-item__answer">{a}</p>}
    </div>
  );
}

function ReviewCard({ review, delayClass }) {
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(review.likes);

  const handleLike = () => {
    if (!liked) {
      setLikesCount((prev) => prev + 1);
      setLiked(true);
    } else {
      setLikesCount((prev) => prev - 1);
      setLiked(false);
    }
  };

  return (
    <div
      className={`home-review-card fade-up ${delayClass}`.trim()}
      style={{
        backgroundColor: "#fff",
        borderRadius: "16px",
        padding: "20px",
        color: "#333",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <div
        className="home-review-card__header"
        style={{ marginBottom: "12px", borderBottom: "none" }}
      >
        <div
          className="home-review-card__author"
          style={{ alignItems: "center", gap: "12px" }}
        >
          <div
            className="home-review-card__avatar"
            style={{
              backgroundColor: "#f0f2f5",
              color: "#666",
              width: "40px",
              height: "40px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
            }}
          >
            {review.avatar}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <p
              className="home-review-card__name"
              style={{
                color: "#111",
                fontWeight: "600",
                fontSize: "15px",
                margin: 0,
              }}
            >
              {review.name}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ display: "flex", color: "#fbbf24" }}>
                {Array.from({ length: review.rating }).map((_, i) => (
                  <Star key={i} size={12} fill="currentColor" stroke="none" />
                ))}
              </div>
              <p
                className="home-review-card__location"
                style={{
                  color: "#666",
                  fontSize: "13px",
                  opacity: 1,
                  margin: 0,
                }}
              >
                {review.location} • {review.date}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: "8px" }}>
        <p
          style={{
            fontWeight: "700",
            fontSize: "16px",
            marginBottom: "4px",
            color: "#111",
          }}
        >
          {review.title}
        </p>
      </div>

      <p
        className="home-review-card__text"
        style={{
          color: "#666",
          fontSize: "15px",
          lineHeight: "1.6",
          fontFamily: "Georgia, 'Times New Roman', Times, serif",
          fontStyle: "normal",
          marginBottom: "16px",
        }}
      >
        {review.text}
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          color: "#008a00",
          fontSize: "13px",
          fontWeight: "500",
          marginBottom: "12px",
        }}
      >
        <CheckCircle size={14} />
        Verified Purchase
      </div>

      {review.image && (
        <img
          src={review.image}
          alt="Customer Review"
          className="home-review-card__image"
          loading="lazy"
          style={{
            borderRadius: "12px",
            width: "100px",
            height: "100px",
            objectFit: "cover",
            marginBottom: "16px",
            display: "block",
          }}
        />
      )}

      <div
        className="home-review-card__footer"
        style={{
          borderTop: "1px solid #eee",
          paddingTop: "12px",
          marginTop: "auto",
        }}
      >
        <div style={{ display: "flex", gap: "16px" }}>
          <button
            className={`home-review-card__like-btn ${liked ? "liked" : ""}`}
            onClick={handleLike}
            aria-label="Like review"
            style={{
              color: liked ? "#008a00" : "#666",
              backgroundColor: "transparent",
              padding: 0,
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
            }}
          >
            <span>👍 Helpful ({likesCount})</span>
          </button>
          <button
            style={{
              color: "#666",
              backgroundColor: "transparent",
              padding: 0,
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <MessageCircle size={14} />
            <span>Reply</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const [scrolled, setScrolled] = useState(false);
  const [couponCode, setCouponCode] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const hasTrackedViewContentRef = useRef(false);

  useScrollReveal(isReady);

  // 动态构建 WA URL，couponCode 变化时自动更新
  const waUrl = useMemo(() => {
    const msg = buildWaMessage(couponCode);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;
  }, [couponCode]);

  const getSearchParam = (param) => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      return params.get(param);
    }

    return null;
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 60;
      setScrolled(isScrolled);

      if (isScrolled && !hasTrackedViewContentRef.current) {
        hasTrackedViewContentRef.current = true;

        const eventType = "ViewContent";
        const eventId = getEventId(eventType);
        const eventData = {
          content_ids: "",
          content_name: "View Content",
          value: "",
          currency: "",
          num_items: 0,
          contents: "",
        };

        FacebookAPI.Facebook(
          eventId,
          eventType,
          eventData,
          createEmptyUserData(),
        );
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 初始化：ad 缓存 → 站点配置 → 像素加载 → fbp/fbc cookie → 优惠券码
  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      // ========== 第 0 步：同步缓存 ad 参数（最高优先级，不阻塞任何异步） ==========
      const adFromQuery = getSearchParam("ad");
      if (adFromQuery && adFromQuery.trim() !== "") {
        LocalStorageUtil.saveAd(adFromQuery);
      }

      // 生成/获取 anonymousUserId（第一时间生成，带在所有通知中）
      const anonymousUserId = LocalStorageUtil.getUserId();

      // ========== 第 1 步：加载站点配置 ==========
      let config = null;
      const phone = WHATSAPP_NUMBER;

      try {
        config = await getConfig();
      } catch (error) {
        console.error("加载配置失败，使用默认值:", error);
      }

      if (cancelled) return;

      // 缓存站点信息到本地
      if (config) {
        LocalStorageUtil.saveSiteInfo(config);
        if (config.pixel) LocalStorageUtil.savePixel(config.pixel);
        if (config.conversion_token)
          LocalStorageUtil.saveFbqToken(config.conversion_token);
        if (config.telegram_group_id)
          LocalStorageUtil.saveTgid(config.telegram_group_id);
      }

      // ========== 第 2 步：加载 Facebook Pixel（确保 fbp/fbc cookie 写入） ==========
      const pixel = config?.pixel || "";
      if (pixel) {
        await loadFbPixel(pixel);
      }

      // ========== 第 3 步：等待 fbp/fbc cookie 写入完成 ==========
      const cookieValues = await waitForFbCookies(["_fbp", "_fbc"], 5, 500);
      const _fbp = cookieValues._fbp || "";
      const _fbc = cookieValues._fbc || "";

      if (cancelled) return;

      // ========== 第 4 步：发送 PageView 事件 + 飞机通知 ==========
      const ad = LocalStorageUtil.getAd?.() || "shop";

      // 发送 Telegram 飞机通知（页面访问）
      sendNotification(
        `📍 页面访问\nuserid: ${anonymousUserId}\n活动: ViewContent\n域名: ${window.location.host}\nad: ${ad}`,
      );

      const pvEventType = "Page View";
      const pvEventId = getEventId(pvEventType);
      const pvEventData = {
        content_ids: "",
        content_name: "Page View",
        value: "",
        currency: "",
        num_items: 0,
        contents: "",
      };
      FacebookAPI.Facebook(
        pvEventId,
        pvEventType,
        pvEventData,
        createEmptyUserData(),
      );

      // ========== 第 5 步：创建优惠券码 ==========
      try {
        const couponEventId = getEventId(FB_TRANSFORM_EVENT_NAME);
        const fbqToken = config?.conversion_token || "";
        const telegramChatId = config?.telegram_group_id || "";

        const params = {
          fbp: _fbp,
          fbc: _fbc,
          eventId: couponEventId,
          ownPhone: phone,
          transformEvent: FB_TRANSFORM_EVENT_NAME,
          pixel: pixel,
          fbqToken: fbqToken,
          telegramChatId: telegramChatId,
          countryCode: COUNTRY_CODE,
        };

        console.log("正在获取优惠券代码...", params);
        const res = await createCouponCodeFormFbData(params);

        if (cancelled) return;

        const couponCodeFromApi = res?.data?.data?.couponCode;
        if (res?.data?.code === 1 && couponCodeFromApi) {
          setCouponCode(couponCodeFromApi);
          console.log("优惠券代码获取成功:", couponCodeFromApi);
        } else {
          console.log("优惠券代码获取失败，API 响应:", res);
        }
      } catch (error) {
        console.error("加载优惠券代码失败:", error);
      }

      // ========== 完成：关闭 loading ==========
      if (!cancelled) {
        setIsReady(true);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleWhatsAppClick = (source, product = null) => {
    const anonymousUserId = LocalStorageUtil.getUserId();
    let ad = getSearchParam("ad");

    if (!ad || ad.trim() === "") {
      ad = LocalStorageUtil.getAd?.() ?? "shop";
    }

    // 发送 Telegram 飞机通知（WA 点击）
    const clickAction = product
      ? `WhatsAppClick - ${source} (${product.title})`
      : `WhatsAppClick - ${source}`;
    sendNotification(
      `📍 用户点击\nuserid: ${anonymousUserId}\n活动: ${clickAction}\n域名: ${window.location.host}\nad: ${ad}\n优惠券码: ${couponCode || "无"}`,
    );

    let activityMessage = `点击 WhatsApp - ${source}`;
    if (product) {
      activityMessage += ` (产品: ${product.title})`;
    }
  };

  const handleViewCollectionClick = () => {
    const userid = LocalStorageUtil.getUserId();
    let ad = getSearchParam("ad");

    if (!ad || ad.trim() === "") {
      ad = LocalStorageUtil.getAd?.() ?? "shop";
    }

    sendNotification(
      `📍 用户点击\nuserid: ${userid}\n活动: ViewCollection\n域名: ${window.location.host}\nad: ${ad}\n优惠券码: ${couponCode || "无"}`,
    );

    // 调用 Facebook View Content 事件
    const eventType = "ViewContent";
    const eventId = getEventId(eventType);
    const eventData = {
      content_ids: "",
      content_name: "View Content",
      value: "",
      currency: "",
      num_items: 0,
      contents: "",
    };

    FacebookAPI.Facebook(eventId, eventType, eventData, createEmptyUserData());
  };

  // loading 状态：拿到优惠券码之前显示全屏加载
  if (!isReady) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#0a0a0a",
          zIndex: 9999,
        }}
      >
        <div style={{ textAlign: "center", color: "#d4af37" }}>
          <div
            style={{
              width: 48,
              height: 48,
              border: "3px solid rgba(212,175,55,0.2)",
              borderTopColor: "#d4af37",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }}
          />
          <p
            style={{
              fontSize: 14,
              letterSpacing: 2,
              textTransform: "uppercase",
            }}
          >
            Loading...
          </p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="home-page">
      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="home-wa-float wa-pulse"
        onClick={() => handleWhatsAppClick("浮动按钮")}
        aria-label="Chat on WhatsApp"
      >
        <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </a>

      <nav
        className={`home-nav ${scrolled ? "home-nav--scrolled" : ""}`.trim()}
      >
        <img
          src={ASSETS.logo}
          alt="LuxeCraft Shop"
          className="home-nav__logo"
        />
        <a
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="home-button home-button--gold home-button--nav"
          onClick={() => handleWhatsAppClick("导航栏按钮")}
        >
          Chat Now
        </a>
      </nav>

      <section className="home-hero">
        <img src={ASSETS.heroBg} alt="" className="home-hero__bg" />
        <div className="home-hero__overlay" />

        <div className="container home-hero__container">
          <div className="home-hero__content">
            <p className="home-kicker">Exclusive US Collection</p>

            <h1 className="home-hero__title">
              Luxury Collection
              <br />
              <em className="home-text-gold-italic">Curated For You</em>
            </h1>

            <div className="gold-rule home-gold-rule--hero" />

            <p className="home-hero__desc">
              Our professional senior stylists will carefully select the perfect
              size and fit for you, providing sincere and expert fashion advice.
              Exclusive access via WhatsApp.
            </p>

            <div className="home-hero__actions">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="home-button home-button--wa"
                onClick={() => handleWhatsAppClick("Hero主按钮")}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Get Full Catalog
              </a>

              <a
                href="#collection"
                className="home-button home-button--ghost"
                onClick={handleViewCollectionClick}
              >
                View Collection
              </a>
            </div>

            <div className="home-hero__badges">
              {heroBadges.map((badge) => (
                <div key={badge} className="home-badge">
                  <span className="home-badge__star">✦</span>
                  {badge}
                </div>
              ))}
            </div>

            <div style={{ marginTop: "2rem" }}>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="home-button home-button--outline"
                onClick={() =>
                  handleWhatsAppClick("Hero View Products List 按钮")
                }
              >
                View Products List
              </a>
            </div>
          </div>
        </div>

        <div className="home-hero__scroll">
          <div className="home-hero__scroll-line" />
        </div>
      </section>

      <section className="home-benefits">
        <div className="container home-benefits__row">
          {benefits.map((item) => (
            <div key={item} className="home-benefits__item">
              <span className="home-benefits__icon">✦</span>
              {item}
            </div>
          ))}
        </div>
      </section>

      <section id="collection" className="home-section">
        <div className="container">
          <div className="home-section__intro fade-up">
            <p className="home-kicker">Luxury Highlights</p>
            <h2 className="home-section__title">
              Latest Luxury & Sneaker Collection
            </h2>
            <div className="gold-rule home-gold-rule--center" />
            <p className="home-section__desc home-section__desc--medium">
              Updated with our newest designer handbags, AJ sneakers, footwear,
              outerwear, and curated accessories. Contact us on WhatsApp to
              check stock, versions, and pricing.
            </p>
          </div>

          <div className="home-product-grid">
            {products.map((product, index) => {
              const isVideo = product.media && product.media.endsWith(".mp4");

              return (
                <a
                  key={product.id}
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`home-product-card product-card hover-gold fade-up ${getDelay80Class(index)}`.trim()}
                  onClick={() => handleWhatsAppClick("产品卡片", product)}
                >
                  <div className="home-product-card__media-wrap">
                    {isVideo ? (
                      <video
                        src={product.media}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="metadata"
                        className="home-product-card__media"
                      />
                    ) : (
                      <img
                        src={product.media}
                        alt={product.title}
                        loading="lazy"
                        className="home-product-card__media"
                      />
                    )}
                    <span className="home-product-card__tag">
                      {product.tag}
                    </span>
                  </div>

                  <div className="home-product-card__body">
                    <h3 className="home-product-card__title">
                      {product.title}
                    </h3>
                    <p className="home-product-card__subtitle">
                      {product.subtitle}
                    </p>
                    <div className="home-product-card__cta">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      Inquire via WhatsApp
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="home-section__action fade-up">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="home-button home-button--gold home-button--large"
              onClick={() => handleWhatsAppClick("产品区底部按钮")}
            >
              Get Exclusive Collection Access
            </a>
          </div>
        </div>
      </section>

      <section className="home-section home-section--alt">
        <div className="container">
          <div className="home-section__intro fade-up">
            <p className="home-kicker">Our Promise</p>
            <h2 className="home-section__title">Why Customers Choose Us</h2>
            <div className="gold-rule home-gold-rule--center" />
          </div>

          <div className="home-feature-grid">
            {trustFeatures.map((feature, index) => {
              const Icon = feature.icon;

              return (
                <div
                  key={feature.title}
                  className={`home-feature-card fade-up ${getDelay80Class(index)}`.trim()}
                >
                  <div className="home-feature-card__icon-wrap">
                    <Icon size={18} className="home-feature-card__icon" />
                  </div>
                  <h3 className="home-feature-card__title">{feature.title}</h3>
                  <p className="home-feature-card__desc">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="container">
          <div className="home-about-grid">
            <div className="home-about__media fade-up">
              <video
                src={ASSETS.showroom}
                autoPlay
                muted
                loop
                playsInline
                className="home-about__video"
              />
              <div className="home-about__frame" />
            </div>

            <div className="home-about__content fade-up home-delay-150-1">
              <p className="home-kicker">About Us</p>
              <h2 className="home-section__title home-section__title--left">
                Your Personal
                <br />
                <em className="home-text-gold-italic">Luxury Concierge</em>
              </h2>
              <div className="gold-rule home-gold-rule--hero" />
              <p className="home-copy">
                We are a dedicated luxury goods concierge service for discerning
                US customers, specializing in top-tier 1:1 replicas. Our team
                curates an exclusive selection of premium designer pieces, from
                iconic handbags to statement footwear and refined outerwear,
                ensuring every detail perfectly matches the original.
              </p>
              <p className="home-copy home-copy--spaced">
                Every client receives personal, one-on-one attention. We believe
                luxury shopping should be effortless, private, and tailored to
                your individual taste with our uncompromising 1:1 replica
                quality.
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="home-button home-button--outline"
                onClick={() => handleWhatsAppClick("About区域按钮")}
              >
                Get Private Assistance
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section home-section--alt">
        <div className="container">
          <div className="home-section__intro fade-up">
            <p className="home-kicker">Testimonials</p>
            <h2 className="home-section__title">What Our Clients Say</h2>
            <div className="gold-rule home-gold-rule--center" />
          </div>

          <div className="home-review-grid">
            {reviews.map((review, index) => (
              <ReviewCard
                key={review.id}
                review={review}
                delayClass={getDelay80Class(index)}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="home-section">
        <div className="container">
          <div className="home-section__intro fade-up">
            <p className="home-kicker">Common Questions</p>
            <h2 className="home-section__title">Frequently Asked Questions</h2>
            <div className="gold-rule home-gold-rule--center" />
          </div>

          <div className="home-faq-list">
            {faqs.map((faq) => (
              <FaqItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>

          <div className="home-section__action fade-up">
            <p className="home-faq__help-text">
              Have more questions? Our team is happy to help.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="home-button home-button--outline"
              onClick={() => handleWhatsAppClick("FAQ区域按钮")}
            >
              Ask Us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      <footer className="home-footer">
        <div className="container">
          <div className="home-footer__content">
            <div className="home-footer__brand">
              <img
                src={ASSETS.logo}
                alt="LuxeCraft Shop"
                className="home-footer__logo"
              />
              <p className="home-footer__text">
                Your dedicated luxury goods concierge for the US market. Premium
                selections, personal service, private communication.
              </p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="home-button home-button--footer"
                onClick={() => handleWhatsAppClick("Footer区域按钮")}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Chat Now
              </a>
            </div>
          </div>

          <div className="home-footer__divider" />
        </div>
      </footer>
    </div>
  );
}
